import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Send, Sparkles } from 'lucide-react';
import type * as React from 'react';
import { Link } from 'react-router-dom';

/**
 * Public landing rendered at `/` for anonymous visitors. No chrome — this
 * is the unauth surface that replaces the AppShell flash + raw AAD bounce
 * that pre-dated this work. Three sections, all built on existing shadcn
 * primitives (`Button`, `Card`) — no new components.
 *
 *   1. Hero — Cotality mark + tagline + primary "Explore" CTA → `/auth/login`
 *   2. Feature grid — three copy-only cards (Browse / Install /
 *      Contribute); icons from lucide
 *   3. Footer — Docs link (mirrors `nav-config.ts:52`'s `DOCS_URL`
 *      DEV/prod switch) + copyright. A Support link is intentionally
 *      omitted until a canonical inbox is provisioned — shipping a
 *      `mailto:` to an unmonitored address would route anon user feedback
 *      into a black hole. Add back once the address exists.
 */

// Mirrors `src/components/sidebar/nav-config.ts:52` — keep both in lockstep.
// Dev hits the Astro dev server on its own origin; prod serves `/docs/`
// from `marketplace-ui/dist/docs/` via SWA.
const DOCS_URL = import.meta.env.DEV ? 'http://localhost:4321/docs/' : '/docs/';

const FEATURES: ReadonlyArray<{
  title: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    title: 'Browse',
    description: 'Discover plugins published by Cotality teams, curated and grouped by category.',
    Icon: Sparkles,
  },
  {
    title: 'Install',
    description: 'One-command install via the marketplace CLI — fast, deterministic, audited.',
    Icon: Download,
  },
  {
    title: 'Contribute',
    description: "Publish your team's plugins for the rest of the org, with review built in.",
    Icon: Send,
  },
];

export function PublicLanding(): React.ReactElement {
  const year = new Date().getFullYear();
  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <main className="flex flex-1 flex-col">
        <section
          className="flex flex-col items-center gap-6 px-6 pt-24 pb-16 text-center"
          aria-labelledby="public-landing-hero"
        >
          <div className="flex flex-col items-center gap-2">
            <img src="/cotality-logo.png" alt="Cotality" className="h-12 w-auto dark:invert" />
            <span className="text-base font-medium tracking-tight text-muted-foreground">
              Marketplace
            </span>
          </div>
          <h1
            id="public-landing-hero"
            className="max-w-2xl text-4xl font-semibold tracking-tight sm:text-5xl"
          >
            The curated catalog of Claude Code plugins for Cotality teams
          </h1>
          <p className="max-w-xl text-base text-muted-foreground sm:text-lg">
            Browse, install, and publish productivity plugins built by Cotality engineers.
          </p>
          <Button asChild size="lg" className="mt-2">
            <Link to="/auth/login">Explore</Link>
          </Button>
        </section>

        <section
          className="mx-auto grid w-full max-w-5xl gap-4 px-6 pb-24 md:grid-cols-3"
          aria-labelledby="public-landing-features"
        >
          <h2 id="public-landing-features" className="sr-only">
            What the marketplace does
          </h2>
          {FEATURES.map(({ title, description, Icon }) => (
            <Card key={title}>
              <CardHeader>
                <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
                <CardTitle className="mt-3 text-lg">{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </CardHeader>
              <CardContent className="pt-0" />
            </Card>
          ))}
        </section>
      </main>

      <footer className="border-t bg-muted/30">
        <div className="mx-auto flex w-full max-w-5xl flex-col items-center justify-between gap-3 px-6 py-6 text-xs text-muted-foreground sm:flex-row">
          <p>&copy; {year} Cotality. All rights reserved.</p>
          <nav aria-label="Footer" className="flex items-center gap-4">
            <a href={DOCS_URL} className="hover:text-foreground" rel="noreferrer">
              Docs
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
